<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '00:00',
  },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const activePanel = ref('summary');
const selectedHour = ref(9);
const selectedMinute = ref(0);
const selectedPeriod = ref('AM');

const hours = Array.from({ length: 12 }, (_, index) => index + 1);
const minutes = Array.from({ length: 12 }, (_, index) => index * 5);
const periods = ['AM', 'PM'];
const COLON = ':';

const normalizeHour = value => {
  if (!value) return 9;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 1 || parsed > 12 ? 9 : parsed;
};

const normalizeMinute = value => {
  if (!value) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseValue = value => {
  if (!value) {
    selectedHour.value = 9;
    selectedMinute.value = 0;
    selectedPeriod.value = 'AM';
    return;
  }

  const [time = '', period = 'AM'] = value.split(' ');
  const [hour = '09', minute = '00'] = time.split(':');

  selectedHour.value = normalizeHour(hour);
  selectedMinute.value = normalizeMinute(minute);
  selectedPeriod.value = periods.includes(period) ? period : 'AM';
};

watch(
  () => props.modelValue,
  value => parseValue(value),
  { immediate: true }
);

const emitValue = () => {
  emit(
    'update:modelValue',
    `${String(selectedHour.value).padStart(2, '0')}:${String(
      selectedMinute.value
    ).padStart(2, '0')} ${selectedPeriod.value}`
  );
};

const displayValue = computed(() => {
  let hour24 = selectedHour.value % 12;

  if (selectedPeriod.value === 'PM') {
    hour24 += 12;
  }

  return `${String(hour24).padStart(2, '0')}:${String(
    selectedMinute.value
  ).padStart(2, '0')}`;
});

const cycleHour = direction => {
  const currentIndex = hours.indexOf(selectedHour.value);
  const nextIndex =
    direction === 'up'
      ? (currentIndex + 1) % hours.length
      : (currentIndex - 1 + hours.length) % hours.length;

  selectedHour.value = hours[nextIndex];
  emitValue();
};

const cycleMinute = direction => {
  const currentIndex = minutes.indexOf(selectedMinute.value);
  const nextIndex =
    direction === 'up'
      ? (currentIndex + 1) % minutes.length
      : (currentIndex - 1 + minutes.length) % minutes.length;

  selectedMinute.value = minutes[nextIndex];
  emitValue();
};

const cyclePeriod = direction => {
  const currentIndex = periods.indexOf(selectedPeriod.value);
  const nextIndex =
    direction === 'up'
      ? (currentIndex + 1) % periods.length
      : (currentIndex - 1 + periods.length) % periods.length;

  selectedPeriod.value = periods[nextIndex];
  emitValue();
};

const selectHour = hour => {
  selectedHour.value = hour;
  emitValue();
  activePanel.value = 'summary';
};

const selectMinute = minute => {
  selectedMinute.value = minute;
  emitValue();
  activePanel.value = 'summary';
};

const selectPeriod = period => {
  selectedPeriod.value = period;
  emitValue();
  activePanel.value = 'summary';
};

const openPicker = () => {
  if (props.disabled) return;
  isOpen.value = true;
  activePanel.value = 'summary';
};

const closePicker = () => {
  isOpen.value = false;
  activePanel.value = 'summary';
};
</script>

<template>
  <div v-on-clickaway="closePicker" class="relative w-full min-w-[8rem]">
    <button
      type="button"
      :disabled="disabled"
      class="flex h-8 w-full items-center justify-between rounded-lg border bg-transparent px-3 text-left text-sm text-n-slate-12 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      :class="
        hasError
          ? 'border-n-ruby-8'
          : 'border-n-slate-6 hover:border-n-slate-7 focus:border-n-brand'
      "
      @click="openPicker"
    >
      <span class="truncate">{{ displayValue || placeholder }}</span>
      <span
        class="i-lucide-chevron-down ml-2 inline-flex h-4 w-4 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[16.5rem] rounded-xl border border-n-weak bg-n-surface-1 p-4 shadow-xl"
    >
      <div
        v-if="activePanel === 'summary'"
        class="grid grid-cols-[1fr_auto_1fr_auto_1fr] gap-3"
      >
        <button
          type="button"
          class="col-start-1 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cycleHour('up')"
        >
          <i class="i-lucide-chevron-up size-4" />
        </button>
        <button
          type="button"
          class="col-start-3 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cycleMinute('up')"
        >
          <i class="i-lucide-chevron-up size-4" />
        </button>
        <button
          type="button"
          class="col-start-5 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cyclePeriod('up')"
        >
          <i class="i-lucide-chevron-up size-4" />
        </button>

        <button
          type="button"
          class="flex h-10 items-center justify-center rounded-md text-xl text-n-slate-12 transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'hour'"
        >
          {{ String(selectedHour).padStart(2, '0') }}
        </button>
        <div
          class="flex h-10 items-center justify-center text-xl text-n-slate-12"
        >
          {{ COLON }}
        </div>
        <button
          type="button"
          class="flex h-10 items-center justify-center rounded-md text-xl text-n-slate-12 transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'minute'"
        >
          {{ String(selectedMinute).padStart(2, '0') }}
        </button>
        <div
          class="flex h-10 items-center justify-center text-xl text-n-slate-12"
        >
          &nbsp;
        </div>
        <button
          type="button"
          class="flex h-10 items-center justify-center rounded-md text-base text-n-brand transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'period'"
        >
          {{ selectedPeriod }}
        </button>

        <button
          type="button"
          class="col-start-1 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cycleHour('down')"
        >
          <i class="i-lucide-chevron-down size-4" />
        </button>
        <button
          type="button"
          class="col-start-3 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cycleMinute('down')"
        >
          <i class="i-lucide-chevron-down size-4" />
        </button>
        <button
          type="button"
          class="col-start-5 flex h-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="cyclePeriod('down')"
        >
          <i class="i-lucide-chevron-down size-4" />
        </button>
      </div>

      <div v-else-if="activePanel === 'hour'" class="flex flex-col gap-3">
        <div class="grid grid-cols-3 gap-3">
          <button
            v-for="hour in hours"
            :key="hour"
            type="button"
            class="flex h-10 items-center justify-center rounded-md text-lg text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': selectedHour === hour }"
            @click="selectHour(hour)"
          >
            {{ String(hour).padStart(2, '0') }}
          </button>
        </div>
        <button
          type="button"
          class="mx-auto flex size-8 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'summary'"
        >
          <i class="i-lucide-clock-3 size-4" />
        </button>
      </div>

      <div v-else-if="activePanel === 'minute'" class="flex flex-col gap-3">
        <div class="grid grid-cols-3 gap-3">
          <button
            v-for="minute in minutes"
            :key="minute"
            type="button"
            class="flex h-10 items-center justify-center rounded-md text-lg text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': selectedMinute === minute }"
            @click="selectMinute(minute)"
          >
            {{ String(minute).padStart(2, '0') }}
          </button>
        </div>
        <button
          type="button"
          class="mx-auto flex size-8 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'summary'"
        >
          <i class="i-lucide-clock-3 size-4" />
        </button>
      </div>

      <div v-else class="flex flex-col gap-3">
        <div class="grid grid-cols-2 gap-3">
          <button
            v-for="period in periods"
            :key="period"
            type="button"
            class="flex h-10 items-center justify-center rounded-md text-base text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2 text-n-brand': selectedPeriod === period }"
            @click="selectPeriod(period)"
          >
            {{ period }}
          </button>
        </div>
        <button
          type="button"
          class="mx-auto flex size-8 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="activePanel = 'summary'"
        >
          <i class="i-lucide-clock-3 size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
